import React, { useState, useEffect, useRef } from "react";
import { Plus, Search, X, ChevronDown } from "lucide-react";
import { useDropdowns } from "./UseDropdowns";

const ClientsDropdown = ({
  value,
  onChange,
  onAddNew,
  placeholder = "Selectează clientul",
  required = false,
  className = "",
  showAddButton = true
}) => {
  const { data, loading, error, refetch } = useDropdowns();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Click outside pentru a închide dropdown-ul
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Filtrare clienți
  const filteredClients = data.clients.filter(client => {
    const searchLower = searchTerm.toLowerCase();
    return (
      client.nume_firma.toLowerCase().includes(searchLower) ||
      (client.cui && client.cui.toLowerCase().includes(searchLower))
    );
  });

  // Găsește clientul selectat
  const selectedClient = value ? data.clients.find(c => c.id === value) : null;

  const handleSelectClient = (client) => {
    onChange(client.id, client);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleClearSelection = (e) => {
    e.stopPropagation();
    onChange(null, undefined);
    setSearchTerm("");
  };

  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <div className="w-full px-4 py-3 border rounded-lg bg-gray-100 text-gray-500">
          Se încarcă clienții...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`relative ${className}`}>
        <div className="w-full px-4 py-3 border rounded-lg bg-red-50 border-red-200 text-red-600">
          Eroare la încărcare
        </div>
        <button
          type="button"
          onClick={refetch}
          className="mt-2 text-sm text-red-600 underline hover:text-red-700"
        >
          Reîncearcă
        </button>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="flex gap-2">
        {/* Dropdown principal */}
        <div className="flex-1 relative">
          <div
            onClick={() => setIsOpen(!isOpen)}
            className="w-full px-4 py-3 border rounded-lg bg-white cursor-pointer flex items-center justify-between hover:border-orange-300 transition-colors"
          >
            <span className={selectedClient ? "text-gray-900" : "text-gray-400"}>
              {selectedClient 
                ? `${selectedClient.nume_firma}${selectedClient.cui ? ` - ${selectedClient.cui}` : ""}`
                : placeholder
              }
            </span>
            <div className="flex items-center gap-2">
              {selectedClient && (
                <button
                  type="button"
                  onClick={handleClearSelection}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <ChevronDown 
                className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </div>

          {/* Dropdown meniu */}
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-80 overflow-hidden">
              {/* Search bar */}
              <div className="p-2 border-b sticky top-0 bg-white">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Caută client..."
                    className="w-full pl-9 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>

              {/* Lista clienți */}
              <div className="overflow-y-auto max-h-64">
                {filteredClients.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-500 text-center">
                    {searchTerm ? "Nu s-au găsit clienți" : "Nu există clienți în sistem"}
                  </div>
                ) : (
                  filteredClients.map((client) => (
                    <div
                      key={client.id}
                      onClick={() => handleSelectClient(client)}
                      className={`px-4 py-3 cursor-pointer hover:bg-orange-50 transition-colors ${
                        value === client.id ? 'bg-orange-100' : ''
                      }`}
                    >
                      <div className="font-medium text-gray-900">{client.nume_firma}</div>
                      {client.cui && (
                        <div className="text-sm text-gray-500">CUI: {client.cui}</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Buton adaugă */}
        {showAddButton && onAddNew && (
          <button
            type="button"
            onClick={onAddNew}
            className="px-3 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center"
            title="Adaugă client nou"
          >
            <Plus className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Hidden input pentru form validation */}
      {required && (
        <input
          type="hidden"
          value={value || ""}
          required={required}
        />
      )}
    </div>
  );
};

export default ClientsDropdown;